import { useEffect, useState, type FC } from "react";
import { IntervalToggleContainer } from "../CronGenerator.style";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select, { type SelectChangeEvent } from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import React from "react";

type CronValues = {
    minute: string;
    hour: string;
    day: string;
    month: string;
    weekDay: string;
};

type DayProps = {
    setCronValues: React.Dispatch<React.SetStateAction<CronValues>>;
    clear: boolean;
};

const WeekDayOptions = {
    EVERY: "Weekday",
    RANGE: "Range",
} as const;

const DayOfWeek: FC<DayProps> = ({ setCronValues, clear }) => {
    const [activeOption, setActiveOption] = useState<string>(WeekDayOptions.EVERY);
    const [error, setError] = useState(false);
    const [prefixError, setPrefixError] = useState(false);
    const [day, setDay] = useState("*");
    const [prefixInterval, setPrefixInterval] = useState("*");
    const [dayRange, setDayRange] = useState({ from: "", to: "" });
    const [dayRangeError, setDayRangeError] = useState({
        fromError: false,
        toError: false,
    });
    const [startAtEnabled, setStartAtEnabled] = useState<boolean>(false);
    useEffect(() => {
        setDay("*");
        setPrefixInterval("*");
        setError(false);
        setPrefixError(false);
        setDayRange({ from: "", to: "" });
        setDayRangeError({ fromError: false, toError: false });
        setStartAtEnabled(false)
    }, [activeOption, clear]);


    const handleOptionSelect = (event: SelectChangeEvent) => {
        setActiveOption(event.target.value);
    };

    const validateDay = (event: React.ChangeEvent<HTMLInputElement>) => {
        const val = Number(event.target.value);
        if (event.target.value === "") {
            setDay("*");
            setError(false);
            setStartAtEnabled(false);
        } else if (val >= 0 && val <= 6) {
            setDay(event.target.value);
            setError(false);
            setStartAtEnabled(true);
        } else {
            setDay("*");
            setError(true);
            setStartAtEnabled(false);
        }
    };

    const addPrefix = (event: React.ChangeEvent<HTMLInputElement>) => {
        const val = Number(event.target.value);
        if (event.target.value === "") {
            setPrefixInterval("*");
            setPrefixError(false);
        } else if (val >= 1 && val <= 6) {
            setPrefixInterval(event.target.value);
            setPrefixError(false);
        } else {
            setPrefixInterval("*");
            setPrefixError(true);
        }
    };

    const handleFromChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const fromVal = Number(event.target.value);
        setDayRange((prev) => ({
            ...prev,
            from: event.target.value,
        }));
        const toNum = Number(dayRange.to);
        const fromError = !(fromVal >= 0 && fromVal <= 6);
        if (!fromError) {
            setStartAtEnabled(true)
        } else {
            setStartAtEnabled(false)
        }
        setDayRangeError({
            fromError,
            toError: !(fromVal <= toNum && toNum <= 6),
        });
    };

    const handleToChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const toVal = Number(event.target.value);
        setDayRange((prev) => ({
            ...prev,
            to: event.target.value,
        }));
        const fromNum = Number(dayRange.from);
        setDayRangeError((prev) => ({
            ...prev,
            toError: !(fromNum <= toVal && toVal <= 6),
        }));
    };

    useEffect(() => {
        if (activeOption === WeekDayOptions.EVERY) {
            const val =
                prefixInterval === "*" || prefixInterval === ""
                    ? day
                    : day === "*" || day === ""
                        ? day
                        : `${day}/${prefixInterval}`;
            setCronValues((prev) => ({
                ...prev,
                weekDay: val,
            }));
        }
    }, [day, prefixInterval, activeOption, setCronValues]);

    useEffect(() => {
        if (activeOption === WeekDayOptions.RANGE) {
            let rangeVal = "*";
            const from = Number(dayRange.from);
            const to = Number(dayRange.to);
            if (from === 0 && to === 0) {
                rangeVal = "*"
            }
            else if (!isNaN(from) && !isNaN(to) && from <= to && to <= 6) {
                rangeVal = `${from}-${to}`;
            }
            setCronValues((prev) => ({
                ...prev,
                weekDay: rangeVal,
            }));
        } else {
            setCronValues((prev) => ({ ...prev, weekDay: "*" }));
        }
    }, [dayRange, activeOption, setCronValues]);

    const renderSelector = () => {
        switch (activeOption) {
            case WeekDayOptions.EVERY:
                return (
                    <>
                        <TextField
                            label="Weekday"
                            variant="standard"
                            sx={{ m: 1, width: "fit-content" }}
                            onChange={validateDay}
                            error={error}
                            helperText={error ? "0 to 6" : "*Sunday(0)"}
                            value={day === "*" ? "" : day}
                        />
                        <TextField
                            label="Start at"
                            variant="standard"
                            disabled={!startAtEnabled}
                            sx={{ m: 1, width: "fit-content" }}
                            onChange={addPrefix}
                            error={prefixError}
                            helperText={prefixError ? "1 to 6" : "*Weekday"}
                            value={prefixInterval === "*" ? "" : prefixInterval}
                        />
                    </>
                );
            case WeekDayOptions.RANGE:
                return (
                    <>
                        <TextField
                            label="From"
                            variant="standard"
                            sx={{ m: 1, width: "fit-content" }}
                            value={dayRange.from}
                            onChange={handleFromChange}
                            error={dayRangeError.fromError}
                            helperText={dayRangeError.fromError ? "0 to 6" : ""}
                        />
                        <TextField
                            label="To"
                            variant="standard"
                            disabled={!startAtEnabled}
                            sx={{ m: 1, width: "fit-content" }}
                            value={dayRange.to}
                            onChange={handleToChange}
                            error={dayRangeError.toError}
                            helperText={
                                dayRangeError.toError
                                    ? `${Number(dayRange.from) < 6 ? Number(dayRange.from) : "0"} to 6`
                                    : " "
                            }
                        />
                    </>
                );
            default:
                return null;
        }
    };

    return (
        <IntervalToggleContainer style={{ alignItems: "start" }}>
            <FormControl variant="standard" sx={{ m: 1, minWidth: 150 }}>
                <InputLabel id="weekday-select-label">Day of Week</InputLabel>
                <Select
                    labelId="weekday-select-label"
                    value={activeOption}
                    onChange={handleOptionSelect}
                >
                    <MenuItem value={WeekDayOptions.EVERY}>{WeekDayOptions.EVERY}</MenuItem>
                    <MenuItem value={WeekDayOptions.RANGE}>{WeekDayOptions.RANGE}</MenuItem>
                </Select>
            </FormControl>
            {renderSelector()}
        </IntervalToggleContainer>
    );
};

export default DayOfWeek;
